import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, tap } from 'rxjs';
import { ITable } from 'src/app/model/table.model';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiServiceListService {

  constructor(private http: HttpClient, private localStorage: LocalStorageService) { }

  prodHttpOptions = {
    headers:
      new HttpHeaders({
        'Access-Control-Allow-Origin': "*",
        'Content-Type': "application/json"
      })
  };

  private cacheData: { zoneData: any };
  private readonly cacheExpiryTime = 5 * 60 * 1000; // 5 minutes

  getPageInfoByPageType(pageName) {
    return this.localStorage.getItem('pageInfo' + pageName);
  }

  setPageInfoPageType(pageName, value) {
    this.localStorage.setItem('pageInfo' + pageName, value);
  }

  imageUpload(params: any, body): Observable<any> {

    if (params.methodType == 'POST') {
      const url = environment.baseAPIHost + environment.imageUploadAPIHost;
      return this.http.post<any>(url, body, this.prodHttpOptions);
    }
  }


  getOrdersTodayData(): Observable<any> {
    const url = environment.baseAPIHost + environment.getOrdersToday;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  getOrdersHistoryData(): Observable<any> {
    const url = environment.baseAPIHost + environment.getOrdersHistory;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  getTodayOrdersData() {
    const url = environment.baseAPIHost + environment.getTodayOrders;
    return this.http.post<any>(url, {}, this.prodHttpOptions);

  }

  getOrderDetailsById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getOrderById + id;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  updateOrderStatus(payload) {
    const url = environment.baseAPIHost + environment.updateOrderDetails;
    return this.http.put<any>(url, payload, this.prodHttpOptions);
  }

  updatePickupDetails(payload) {
    const url = environment.baseAPIHost + environment.updatePickupDetails;
    return this.http.post<any>(url, payload, this.prodHttpOptions);
  }

  getProductDetailsByNameBarcode(body): Observable<any> {
    // {name, barcode}
    const url = environment.baseAPIHost + environment.getProductLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);

  }

  getProductDetailsForStock(body): Observable<any> {
    const url = environment.baseAPIHost + environment.getProductsStockLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);

  }

  validateUpdateOrderPacked(body): Observable<any> {
    const url = environment.baseAPIHost + environment.orderStatusPacked;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  updateKitchenOrderPacked(body) {
    const url = environment.baseAPIHost + environment.orderStatusPackedKitchen;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  showRetailerStock() {
    const url = environment.baseAPIHost + environment.showProductsStock;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  showRentalProductsStock() {
    const url = environment.baseAPIHost + environment.showRentalProductsStock;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  processRentalProductReturn(body) {
    const url = environment.baseAPIHost + environment.processRentalProductReturn;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  showInhouseProductsStock() {
    const url = environment.baseAPIHost + environment.showInhouseProductsStock;
    return this.http.post<any>(url, {}, this.prodHttpOptions);
  }

  editProductAvailability(body) {
    const url = environment.baseAPIHost + environment.editProductAvailability;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  editProductZonePricing(body) {
    const url = environment.baseAPIHost + environment.editProductZonePricing;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  addStockDetails(payload) {
    const url = environment.baseAPIHost + environment.addStockDetails;
    return this.http.post<any>(url, payload, this.prodHttpOptions);
  }

  getBulkOrderAgents() {
    const url = environment.baseAPIHost + environment.getBulkOrderAgents;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  assignBulkOrdersToAgent(payload) {
    const url = environment.baseAPIHost + environment.assignBulkOrderstoAgent;
    return this.http.post<any>(url, payload, this.prodHttpOptions);
  }

  // Product Alerts Management 
  getProductAlertsDetails(payload): Observable<any> {
    let queryString = '';
    if (payload.page) {
      queryString += `limit=${payload.limit}&page=${payload.page}`;
    }
    const url = environment.baseAPIHost + environment.getProductAlerts + '?' + queryString;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getProductAlertsCountDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getProductAlertsCount;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateAlertReadStatus(alertId: string) {
    const url = environment.baseAPIHost + environment.updateProductAlerts + '/' + alertId + '/read';
    return this.http.patch<any>(url, {}, this.prodHttpOptions);
  }


  // Order Management

  getOrdersFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getRetailerOrderFiltersLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getOrdersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getRetailerOrderLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getRetailerOrderById(orderId) {
    const url = environment.baseAPIHost + environment.getRetailerOrderByIdLink + '/' + orderId;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierDeliveryFilters(): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierDeliveryFiltersLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getSupplierDeliveries(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getSupplierDeliveriesLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getSupplierDeliveryById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getSupplierDeliveryByIdLink + '/' + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  validateSupplierDelivery(id: string, body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.validateSupplierDeliveryLink + '/' + id + '/validate-receipt';
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getRetailerOrdersApproved() {
    const url = environment.baseAPIHost + environment.getRetailerOrdersApproved;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  closerRetailerOrder(body: { _id: string }) {
    const url = environment.baseAPIHost + environment.closeRetailerOrder;
    return this.http.post<ITable>(url, body, this.prodHttpOptions);
  }

  createRetailerOrder(body) {
    const url = environment.baseAPIHost + environment.createRetailerOrder;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  saveStoreDetails(body) {
    const url = environment.baseAPIHost + environment.saveStoreDetails;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getStockMovementFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getStockMovementFiltersLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getStockMovementTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getStockMovementLink;
    parameter.body.filteredValue.push({ adjustmentType: parameter.type });
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }


  getUserOrdersFilterData(): Observable<any> {
    const url = environment.baseAPIHost + environment.getUserOrdersFiltersLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getUserOrdersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getUserOrdersLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getUserOrderById(orderId) {
    const url = environment.baseAPIHost + environment.getUserOrderByIdLink + '/' + orderId;
    return this.http.get<any>(url, this.prodHttpOptions);
  }


  getStoreOrdersFilterData(): Observable<any> {
    const url = environment.baseAPIHost + environment.getStoreOrdersFiltersLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getStoreOrdersTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getStoreOrdersLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getStoreOrderById(orderId) {
    const url = environment.baseAPIHost + environment.getStoreOrderByIdLink + '/' + orderId;
    return this.http.get<any>(url, this.prodHttpOptions);
  }




  plCheckStatus(body) {
    const url = environment.baseAPIHost + environment.plCheckStatus;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  regenratePL(body) {
    const url = environment.baseAPIHost + environment.regenratePL;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  refundInitiate(body) {
    const url = environment.baseAPIHost + environment.refundInitiate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  refundCheckStatus(body) {
    const url = environment.baseAPIHost + environment.refundCheckStatus;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  createReplacement(body) {
    const url = environment.baseAPIHost + environment.createReplacement;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }


  cancelReplacement(body) {
    const url = environment.baseAPIHost + environment.cancelReplacement;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Delivery Agent Management
  addDeliveryAgentAPI(body) {
    const url = environment.baseAPIHost + environment.addDeliveryAgentLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getDeliveryAgentFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getDeliveryAgentFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getDeliveryAgentTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getDeliveryAgentLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getDeliveryAgentById(id): Observable<any> {
    const url = environment.baseAPIHost + environment.getDeliveryAgentByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateDeliveryAgentAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editDeliveryAgentLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getDeliveryAgentOrderStats(body) {
    const url = environment.baseAPIHost + environment.deliveryAgentOrderSats;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getDeliveryAgentPayouts(body) {
    const url = environment.baseAPIHost + environment.deliveryAgentPayouts;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  deliveryAgentPaymentUpdate(body) {
    const url = environment.baseAPIHost + environment.deliveryAgentPaymentUpdate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  // Service Provider Management
  addServiceProviderAPI(body) {
    const url = environment.baseAPIHost + environment.addServiceProviderLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getServiceProviderFilterDetails(): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProviderFilterLink;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  getServiceProviderTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getServiceProviderLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getServiceProviderById(id): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProviderByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  updateServiceProviderAPI(body): Observable<any> {
    const url = environment.baseAPIHost + environment.editServiceProviderLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  getServiceProvidersByCategory(body): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceProvidersByCategoryLink;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getServiceProviderOrderStats(body) {
    const url = environment.baseAPIHost + environment.getServiceProviderOrderSats;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getServiceProviderPayouts(body) {
    const url = environment.baseAPIHost + environment.getServiceProviderPayouts;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  serviceProviderPaymentUpdate(body) {
    const url = environment.baseAPIHost + environment.serviceProviderPaymentUpdate;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }

  getServiceBookingTableData(parameter: any): Observable<ITable> {
    const url = environment.baseAPIHost + environment.getServiceBookingsLink;
    return this.http.post<ITable>(url, parameter.body, this.prodHttpOptions);
  }

  getServiceBookingById(id: string): Observable<any> {
    const url = environment.baseAPIHost + environment.getServiceBookingByIdLink + "/" + id;
    return this.http.get<any>(url, this.prodHttpOptions);
  }

  assignServiceProviderToBooking(body: any): Observable<any> {
    const url = environment.baseAPIHost + environment.assignServiceProviderToBookingLink;
    return this.http.put<any>(url, body, this.prodHttpOptions);
  }

  phoneUserValidation(body) {
    const url = environment.baseAPIHost + environment.userPhoneValidation;
    return this.http.post<any>(url, body, this.prodHttpOptions);
  }




  // Dashboard HTTP Call methos
  getDashboardSummary(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardSummaryAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardTrends(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardTrendsAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardOrdersBreakdown(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.dashboardOrdersBreakdownAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDashboardPaymentSummary(query: any): Observable<any> {
    // Note: env key spelled "dashboarPaymentSummaryAPI" in your message—use your exact env key
    const url = environment.baseAPIHost + environment.dashboarPaymentSummaryAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDeliveryPerformance(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.deliveryPerformanceAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

  getDeliveryAlerts(query: any): Observable<any> {
    const url = environment.baseAPIHost + environment.deliveryAlertsAPI;
    return this.http.get<any>(url, { ...this.prodHttpOptions, params: this.buildParams(query) });
  }

   getZonesList(forceRefresh = false): Observable<any> {
    const url = environment.baseAPIHost + environment.getZoneListAPI;

    const now = Date.now();
    const cached = this.cacheData?.zoneData;

    if (!forceRefresh && cached && cached.expiresAt > now) {
      return of(cached.data);
    }

    return this.http.get<any>(url, this.prodHttpOptions).pipe(
      tap((data) => {
        if (data.success) {
          this.cacheData = { ...this.cacheData, zoneData: { data: data, expiresAt: now + this.cacheExpiryTime } };
        }
      })
    );
  }

  fetchDashboardAll(query: any): Observable<{
    summary: any;
    trends: any;
    ordersBreakdown: any;
    paymentSummary: any;
    deliveryPerformance: any;
    // inventoryAlerts: any;
  }> {
    return forkJoin({
      summary: this.getDashboardSummary(query),
      trends: this.getDashboardTrends(query),
      ordersBreakdown: this.getDashboardOrdersBreakdown(query),
      paymentSummary: this.getDashboardPaymentSummary(query),
      deliveryPerformance: this.getDeliveryPerformance(query),
      // inventoryAlerts: this.getDeliveryAlerts(query),
    });
  }
  private buildParams(q: Record<string, any>): HttpParams {
    let p = new HttpParams();
    for (const [k, v] of Object.entries(q)) {
      if (v == null || v === '') continue;
      p = p.set(k, v instanceof Date ? v.toISOString() : String(v));
    }
    return p;
  }

}
